import {
  addDays,
  compareDateOnly,
  dateOnly,
  daysBetween,
  isoWeekday,
  type DateOnly,
} from './date-only.ts';
import type { WorkWeek } from './entities/company.ts';
import type { WorkdayKind } from './entities/schedule.ts';

export interface WorkCalendar {
  readonly workWeek: WorkWeek;
  /** Keyed by calendar date. A missing date just follows the working week. */
  readonly exceptions: Readonly<Record<string, WorkdayKind>>;
}

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const satisfies readonly (keyof WorkWeek)[];

/**
 * A sanity bound on walking day by day, not a limit on real schedules.
 *
 * It was 800 — about two years — and a 500-item schedule spans longer than
 * that, so the guard fired on perfectly ordinary data. The guard that matters
 * is `assertSomeWorkingDay`, which now checks the working week directly
 * rather than inferring it from a failed scan, so this only has to be past
 * anything a construction schedule could reach.
 */
const MAX_SCAN_DAYS = 20_000;

/** Whether the working week alone says this day is worked. */
function patternWorks(workWeek: WorkWeek, date: DateOnly): boolean {
  // isoWeekday is 1-based from Monday, matching WEEKDAY_KEYS.
  return workWeek[WEEKDAY_KEYS[isoWeekday(date) - 1]!];
}

function workdaysPerWeek(workWeek: WorkWeek): number {
  return WEEKDAY_KEYS.filter((key) => workWeek[key]).length;
}

export function isWorkingDay(calendar: WorkCalendar, date: DateOnly): boolean {
  const exception = calendar.exceptions[dateOnly(date)];
  if (exception) return exception === 'working';
  return patternWorks(calendar.workWeek, date);
}

/**
 * Refuses a calendar on which no work could ever happen.
 *
 * Asked of the working week directly: a week with a working day in it can
 * never starve a scan, whatever the exceptions say. Only a completely closed
 * week has to look for an opened day, and then a bounded window is enough —
 * a calendar whose only working day is years away is a misconfiguration, not
 * a schedule.
 */
function assertSomeWorkingDay(calendar: WorkCalendar, from: DateOnly): void {
  if (workdaysPerWeek(calendar.workWeek) > 0) return;

  for (let offset = 0; offset < 800; offset += 1) {
    if (isWorkingDay(calendar, addDays(from, offset))) return;
  }
  throw new RangeError(
    'This calendar has no working days. Check the working week and workday exceptions.',
  );
}

/** The first working day on or after `date`. */
export function nextWorkingDay(calendar: WorkCalendar, date: DateOnly): DateOnly {
  assertSomeWorkingDay(calendar, date);
  let candidate = dateOnly(date);
  while (!isWorkingDay(calendar, candidate)) {
    candidate = addDays(candidate, 1);
  }
  return candidate;
}

/**
 * Move a date by a number of working days, in either direction.
 *
 * Used for dependency lag, where a negative value is legitimate: "start three
 * working days before the predecessor finishes".
 *
 * Found by binary search over `workingDaysBetween` rather than by walking.
 * Walking was fine for a lag of three days and ruinous for the critical
 * path, which asks how far a whole schedule's float reaches — 500 items took
 * 870ms, most of it re-validating the same dates one day at a time. The
 * search is sound because the count of working days only ever rises as the
 * distance grows.
 */
export function addWorkingDays(
  calendar: WorkCalendar,
  date: DateOnly,
  workingDays: number,
): DateOnly {
  if (!Number.isInteger(workingDays)) {
    throw new TypeError(`Working days must be whole, received ${workingDays}`);
  }
  assertSomeWorkingDay(calendar, date);
  const from = dateOnly(date);
  if (workingDays === 0) return from;

  const wanted = Math.abs(workingDays);
  const forward = workingDays > 0;

  /**
   * Working days strictly between `from` and `candidate`, inclusive of
   * `candidate` itself. Monotone in the distance from `from`, which is what
   * makes the search below sound.
   */
  const reached = (offset: number): number =>
    forward
      ? workingDaysBetween(calendar, addDays(from, 1), addDays(from, offset))
      : workingDaysBetween(calendar, addDays(from, -offset), addDays(from, -1));

  // Grow a bound until it covers the ask, doubling rather than stepping: a
  // closed week with one opened day needs a long reach, and a normal
  // calendar reaches it on the first try.
  let high = Math.max(1, Math.ceil((wanted * 7) / Math.max(workdaysPerWeek(calendar.workWeek), 1)));
  while (reached(high) < wanted) {
    high *= 2;
    if (high > MAX_SCAN_DAYS) {
      throw new RangeError('Could not find enough working days on this calendar');
    }
  }

  // The smallest offset whose count reaches the target. That day is
  // necessarily a working day, because the count only rises on one.
  let low = 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (reached(middle) >= wanted) high = middle;
    else low = middle + 1;
  }

  return addDays(from, forward ? low : -low);
}

/**
 * The last day of a span that begins on the first working day on or after
 * `start` and contains `workDays` working days.
 *
 * Counting is inclusive: a one-day item starts and ends the same day. A start
 * that lands on a weekend or holiday rolls forward rather than shortening the
 * work, because "starts Saturday, two days" means two days of actual work.
 *
 * This is the single calculation the whole schedule rests on — every bar in
 * the Gantt, every dependency, every baseline comparison — so it is kept here
 * rather than in a component, and `workingDaysBetween` is its inverse.
 */
export function endDateFor(calendar: WorkCalendar, start: DateOnly, workDays: number): DateOnly {
  if (!Number.isInteger(workDays)) {
    throw new TypeError(`Work days must be a whole number, received ${workDays}`);
  }
  if (workDays < 1) {
    throw new RangeError(`A schedule item needs at least one work day, received ${workDays}`);
  }
  const first = nextWorkingDay(calendar, start);
  return workDays === 1 ? first : addWorkingDays(calendar, first, workDays - 1);
}

/**
 * Working days in the inclusive span, or zero when the span is backwards.
 *
 * Counted arithmetically rather than day by day: whole weeks multiply out,
 * at most six leftover days are checked individually, and the exceptions are
 * then applied as corrections. A job holds tens of exceptions, so the cost
 * does not grow with the length of the span.
 *
 * It used to walk every day, which capped it at about two years and made it
 * the slowest part of the critical path on a long schedule — the one place
 * where a 500-item job stopped working rather than merely slowing down.
 */
export function workingDaysBetween(calendar: WorkCalendar, start: DateOnly, end: DateOnly): number {
  if (compareDateOnly(start, end) > 0) return 0;

  const from = dateOnly(start);
  const to = dateOnly(end);
  const totalDays = daysBetween(from, to) + 1;
  const weeks = Math.floor(totalDays / 7);

  let count = weeks * workdaysPerWeek(calendar.workWeek);

  // The leftover days of a partial week, six at most. The weekday is
  // advanced arithmetically rather than by building a date per day: this is
  // the innermost loop of every schedule calculation, and constructing a
  // Date only to ask which day of the week it is dominated the cost.
  const tailDays = totalDays - weeks * 7;
  if (tailDays > 0) {
    let weekday = isoWeekday(addDays(from, weeks * 7));
    for (let offset = 0; offset < tailDays; offset += 1) {
      if (calendar.workWeek[WEEKDAY_KEYS[weekday - 1]!]) count += 1;
      weekday = weekday === 7 ? 1 : weekday + 1;
    }
  }

  // Exceptions only ever differ from the pattern, so each one inside the span
  // is worth exactly one day either way.
  for (const [date, kind] of Object.entries(calendar.exceptions)) {
    if (compareDateOnly(date, from) < 0 || compareDateOnly(date, to) > 0) continue;
    const pattern = patternWorks(calendar.workWeek, date);
    if (kind === 'working' && !pattern) count += 1;
    else if (kind === 'nonWorking' && pattern) count -= 1;
  }

  return count;
}

/** Build a calendar from a company working week plus the job's exceptions. */
export function workCalendar(
  workWeek: WorkWeek,
  exceptions: Readonly<Record<string, WorkdayKind>> = {},
): WorkCalendar {
  return { workWeek, exceptions };
}
